<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useRoute, useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { useLogin } from '@/composables/api/useAuthApi';
import { AxiosError } from 'axios';

interface LoginErrors {
  email?: string[];
  password?: string[];
  [key: string]: string[] | undefined;
}


// Source the version from the Vite-define injected at build time (mirrors NavUser).
// `import.meta.env.VITE_APP_VERSION` was never set in any .env, so the badge rendered as just "v".
const appVersion = computed(() => __APP_VERSION__);
const route = useRoute();
const router = useRouter();
const { mutate: login, isPending: isLoggingIn } = useLogin();

const passwordResetLinkSent = route.query.password_reset_link_sent;
const passwordReset = route.query.password_reset;
const errors = ref<LoginErrors>({});
const email = ref('');
const password = ref('');

function resetForm() {
  email.value = '';
  password.value = '';
  errors.value = {};
}

/**
 * The `?redirect=` value, when it is a page of this app to go back to, else null.
 * It must start with one `/`. `//host` and `/\host` are read by browsers as
 * another site, so an attacker could forge them to send someone off the app
 * after they sign in. An account page such as sign-out is refused too, because
 * going back to it would sign the person straight out again.
 */
function sameSiteRedirectPath(rawRedirect: unknown): string | null {
  if (typeof rawRedirect !== 'string' || !rawRedirect.startsWith('/')) return null;
  if (rawRedirect.startsWith('//') || rawRedirect.startsWith('/\\') || rawRedirect.startsWith('/account/')) return null;
  return rawRedirect;
}

const handleSubmit = async () => {
  login(
    {
      email: email.value,
      password: password.value,
    },
    {
      onSuccess: () => {
        // No explicit loadUser here: the router's auth guard loads the user
        // during the navigation below, so awaiting it first would just add a
        // second serial /api/user round-trip before the dashboard appears.
        resetForm();
        // Honor a `?redirect=` query param so deep links (e.g. share-view
        // URLs at `/views/import?d=...`) survive an auth bounce — the router
        // guard, and the 401 handler in services/http.ts, wrote the page the
        // person was on into this query when they sent them here.
        const redirectPath = sameSiteRedirectPath(route.query.redirect);
        if (redirectPath) {
          router.push(redirectPath);
        } else {
          router.push({ name: 'home' });
        }
      },
      onError: (error: unknown) => {
        // Clear any previous errors
        errors.value = {};
        
        // Only handle validation errors here, other errors are handled by composable
        if (error instanceof AxiosError && error.response?.status === 422) {
          const errorData = error.response.data;
          if (errorData.errors) {
            errors.value = errorData.errors;
          }
        }
      }
    }
  );
};
</script>

<template>
  <Card class="px-6 py-4">
    <form @submit.prevent="handleSubmit">
      <CardHeader class="pb-2">
        <p
          v-if="passwordResetLinkSent"
          class="mt-2 font-medium text-sm text-green-600"
        >
          We have emailed your password reset link.
        </p>
        <p 
          v-if="passwordReset" 
          class="mt-2 font-medium text-sm text-green-600"
        >
          Your password has been reset.
        </p>
      </CardHeader>
      
      <CardContent class="space-y-4">        
        <div class="space-y-2">
          <InputLabel for="email" value="Email" />
          <Input
            id="email"
            type="email"
            class="block w-full"
            required
            autofocus
            autocomplete="username"
            autocapitalize="none"
            spellcheck="false"
            name="email"
            :modelValue="email"
            @update:modelValue="(value: string | number) => email = String(value)"
            :disabled="isLoggingIn"
            :class="{ 'border-red-500': errors['email'] }"
          />
          <InputError
            v-if="errors['email']"
            class="mt-1"
            :message="errors['email'][0]"
          />
        </div>

        <div class="space-y-2">
          <InputLabel for="password" value="Password" />
          <Input
            id="password"
            type="password"
            class="block w-full"
            required
            autocomplete="current-password"
            name="password"
            :modelValue="password"
            @update:modelValue="(value: string | number) => password = String(value)"
            :disabled="isLoggingIn"
            :class="{ 'border-red-500': errors['password'] }"
          />
          <InputError
            v-if="errors['password']"
            class="mt-1"
            :message="errors['password'][0]"
          />
        </div>

      </CardContent>
      
      <CardFooter class="flex justify-end items-center space-x-4 pt-6">
        <router-link
          to="forgot-password"
          v-slot="{ navigate }"
          custom
        >
        <Button type="button" variant="link" @click="navigate">
            Forgot your password?
          </Button>
        </router-link>
        <Button 
          type="submit"
          :disabled="isLoggingIn"
          :loading="isLoggingIn"
        >
          Log in
        </Button>
      </CardFooter>
    </form>
  </Card>
   <!-- Version number in bottom left corner -->
   <div class="fixed bottom-4 left-4 z-50">
    <span class="text-xs text-gray-500">v{{ appVersion }}</span>
  </div>
</template>